"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SiderContext = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var react_1 = require("react");
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var omit_1 = tslib_1.__importDefault(require("rc-util/lib/omit"));
var config_provider_1 = require("../config-provider");
var DirectionLeft_1 = require("../Icons/DirectionLeft");
var DirectionRight_1 = require("../Icons/DirectionRight");
var OperateOutline_1 = require("../Icons/OperateOutline");
var isNumeric_1 = tslib_1.__importDefault(require("../util/isNumeric"));
var layout_1 = require("./layout");
var dimensionMaxMap = {
    xs: '479.98px',
    sm: '575.98px',
    md: '767.98px',
    lg: '991.98px',
    xl: '1199.98px',
    xxl: '1599.98px',
};
exports.SiderContext = React.createContext({});
var generateId = (function () {
    var i = 0;
    return function (prefix) {
        if (prefix === void 0) { prefix = ''; }
        i += 1;
        return "".concat(prefix).concat(i);
    };
})();
var Sider = function (_a) {
    var customizePrefixCls = _a.prefixCls, className = _a.className, trigger = _a.trigger, children = _a.children, _b = _a.defaultCollapsed, defaultCollapsed = _b === void 0 ? false : _b, _c = _a.theme, theme = _c === void 0 ? 'dark' : _c, _d = _a.style, style = _d === void 0 ? {} : _d, _e = _a.collapsible, collapsible = _e === void 0 ? false : _e, _f = _a.reverseArrow, reverseArrow = _f === void 0 ? false : _f, _g = _a.width, width = _g === void 0 ? 224 : _g, _h = _a.collapsedWidth, collapsedWidth = _h === void 0 ? 80 : _h, zeroWidthTriggerStyle = _a.zeroWidthTriggerStyle, breakpoint = _a.breakpoint, onCollapse = _a.onCollapse, onBreakpoint = _a.onBreakpoint, props = tslib_1.__rest(_a, ["prefixCls", "className", "trigger", "children", "defaultCollapsed", "theme", "style", "collapsible", "reverseArrow", "width", "collapsedWidth", "zeroWidthTriggerStyle", "breakpoint", "onCollapse", "onBreakpoint"]);
    var siderHook = (0, react_1.useContext)(layout_1.LayoutContext).siderHook;
    var _j = tslib_1.__read((0, react_1.useState)('collapsed' in props ? props.collapsed : defaultCollapsed), 2), collapsed = _j[0], setCollapsed = _j[1];
    var _k = tslib_1.__read((0, react_1.useState)(false), 2), below = _k[0], setBelow = _k[1];
    (0, react_1.useEffect)(function () {
        if ('collapsed' in props) {
            setCollapsed(props.collapsed);
        }
    }, [props.collapsed]);
    var handleSetCollapsed = function (value, type) {
        if (!('collapsed' in props)) {
            setCollapsed(value);
        }
        if (onCollapse) {
            onCollapse(value, type);
        }
    };
    // ========================= Responsive =========================
    var responsiveHandlerRef = (0, react_1.useRef)();
    responsiveHandlerRef.current = function (mql) {
        setBelow(mql.matches);
        if (onBreakpoint) {
            onBreakpoint(mql.matches);
        }
        if (collapsed !== mql.matches) {
            handleSetCollapsed(mql.matches, 'responsive');
        }
    };
    (0, react_1.useEffect)(function () {
        function responsiveHandler(mql) {
            return responsiveHandlerRef.current(mql);
        }
        var mql;
        if (typeof window !== 'undefined') {
            var matchMedia_1 = window.matchMedia;
            // @ts-ignore
            if (matchMedia_1 && breakpoint && breakpoint in dimensionMaxMap) {
                mql = matchMedia_1("(max-width: ".concat(dimensionMaxMap[breakpoint], ")"));
                try {
                    mql.addEventListener('change', responsiveHandler);
                }
                catch (error) {
                    mql.addListener(responsiveHandler);
                }
                responsiveHandler(mql);
            }
        }
        return function () {
            try {
                mql === null || mql === void 0 ? void 0 : mql.removeEventListener('change', responsiveHandler);
            }
            catch (error) {
                mql === null || mql === void 0 ? void 0 : mql.removeListener(responsiveHandler);
            }
        };
    }, []);
    (0, react_1.useEffect)(function () {
        var uniqueId = generateId('ant-sider-');
        siderHook.addSider(uniqueId);
        return function () { return siderHook.removeSider(uniqueId); };
    }, []);
    var toggle = function () {
        handleSetCollapsed(!collapsed, 'clickTrigger');
    };
    var getPrefixCls = (0, react_1.useContext)(config_provider_1.ConfigContext).getPrefixCls;
    var renderSider = function () {
        var _a;
        var prefixCls = getPrefixCls('layout-sider', customizePrefixCls);
        var divProps = (0, omit_1.default)(props, ['collapsed']);
        var rawWidth = collapsed ? collapsedWidth : width;
        // use "px" as fallback unit for width
        var siderWidth = (0, isNumeric_1.default)(rawWidth) ? "".concat(rawWidth, "px") : String(rawWidth);
        // special trigger when collapsedWidth == 0
        var zeroWidthTrigger = parseFloat(String(collapsedWidth || 0)) === 0 ? (React.createElement("span", { "data-testid": "sider_trigger1", onClick: toggle, className: (0, classnames_1.default)("".concat(prefixCls, "-zero-width-trigger"), "".concat(prefixCls, "-zero-width-trigger-").concat(reverseArrow ? 'right' : 'left')), style: zeroWidthTriggerStyle }, trigger || React.createElement(OperateOutline_1.OperateOutline, null))) : null;
        var iconObj = {
            expanded: reverseArrow ? React.createElement(DirectionRight_1.DirectionRight, null) : React.createElement(DirectionLeft_1.DirectionLeft, null),
            collapsed: reverseArrow ? React.createElement(DirectionLeft_1.DirectionLeft, null) : React.createElement(DirectionRight_1.DirectionRight, null),
        };
        var status = collapsed ? 'collapsed' : 'expanded';
        var defaultTrigger = iconObj[status];
        var triggerDom = trigger !== null
            ? zeroWidthTrigger || (React.createElement("div", { "data-testid": "sider_trigger2", className: "".concat(prefixCls, "-trigger"), onClick: toggle, style: { width: siderWidth } }, trigger || defaultTrigger))
            : null;
        var divStyle = tslib_1.__assign({ flex: "0 0 ".concat(siderWidth), maxWidth: siderWidth, minWidth: siderWidth, width: siderWidth }, style);
        var siderCls = (0, classnames_1.default)(prefixCls, "".concat(prefixCls, "-").concat(theme), (_a = {},
            _a["".concat(prefixCls, "-collapsed")] = !!collapsed,
            _a["".concat(prefixCls, "-has-trigger")] = collapsible && trigger !== null && !zeroWidthTrigger,
            _a["".concat(prefixCls, "-below")] = !!below,
            _a["".concat(prefixCls, "-zero-width")] = parseFloat(siderWidth) === 0,
            _a), className);
        return (React.createElement("aside", tslib_1.__assign({ className: siderCls }, divProps, { style: divStyle }),
            React.createElement("div", { className: "".concat(prefixCls, "-children") }, children),
            collapsible || (below && zeroWidthTrigger) ? triggerDom : null));
    };
    return (React.createElement(exports.SiderContext.Provider, { value: {
            siderCollapsed: collapsed,
            collapsedWidth: collapsedWidth,
        } }, renderSider()));
};
exports.default = Sider;
//# sourceMappingURL=Sider.js.map